import { useNavigate, useParams } from "react-router-dom";
import type * as L from "leaflet";
import { useTranslation } from "react-i18next";

import { LEZForm, type LEZFormData } from "src/forms/LEZForm";
import { useLEZ, useLEZUpdateMutation } from "src/hooks";
import { useScenario } from "src/hooks/useScenario";
import { isDisabled, isReadOnly } from "src/utils/permission.utils";
import { Box, Button, Grid, Typography } from "@mui/material";

interface Props {
  map: L.Map;
  onClose: () => void;
}

export function LEZEditPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId = "", scenarioId = "", lezId = "" } = useParams();
  const { data: scenario } = useScenario(scenarioId);
  const { data: lez } = useLEZ(scenarioId, lezId);
  const updateMutation = useLEZUpdateMutation(scenarioId, lezId);

  async function handleSave(values: LEZFormData) {
    await updateMutation.mutateAsync(values);
  }

  function handleManageVehicleRestrictions() {
    const url = `/cases/${caseId}/scenarios/${scenarioId}/lezs/${lezId}/fleet-config`;
    navigate(url);
  }

  return render();

  function render() {
    if (!scenario || !lez) return null;

    return (
      <Box display="flex" flexDirection="column">
        <LEZForm
          map={props.map}
          title={t("label.LEZDetails")}
          data={{
            name: lez?.name,
            polygon_frontendSrid: lez.polygon_frontendSrid,
            predefinedFleet: lez.predefinedFleet,
          }}
          isLoading={updateMutation.isLoading}
          errors={updateMutation.error?.response?.data}
          readOnly={isReadOnly(scenario.permissions.updateLEZ)}
          onClose={() => props.onClose()}
          onSubmit={handleSave}
        />

        <Box py={3} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h3">{t("label.restrictions")}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Button fullWidth color="primary" variant="text" onClick={handleManageVehicleRestrictions}>
              {isDisabled(scenario.permissions.updateLEZ)
                ? t("label.viewVehicleRestrictions")
                : t("label.manageVehicleRestrictions")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  }
}
